import React, { useState, useEffect } from "react";
import {
	getFunctions,
	getGrades,
	getZones,
	getJobLocations,
	getEmployeeDetails,
	getDepartments,
	getRoleType,
	getJobCategory,
	getGenderType,
	getEmployeeSuggestions,
	getRoleCategory,
	GetProfile,
} from "../../../../Auth/Services/metadataService";
import { createJobOpening } from "../Services/jobService";
import NormalAlert from "../../../../Alerts/NormalAlert";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import bgImage from "../../../../../Assets/bg3.jpg";
import AddInterviewerModal from "../Modal/AddInterviewerModal";
import { Trash2 } from "lucide-react";

// Author: Harshada Bhavsar (18/7/2025)
// API: Create Job Opening for Employee
const PostJobForm = ({ onSubmit, onCancel }) => {
	const [formData, setFormData] = useState({
		jobTitle: "",
		reportingManagerID: "",
		reportingManager: "",
		functionalManagerID: "",
		functionalManager: "",
		function: "",
		functionID: 0,
		grade: "",
		roleName: "",
		jobCategory: "",
		zone: "",
		jobLocation: "",
		offeredSalary: "",
		minimumQualification: "",
		maximumExperience: "",
		minimumExperience: "",
		genderPreference: "",
		positionType: "",
		requisitionDate: "",
		jobDescription: "",
		department: "",
		numberOfOpenings: 0,
		roleType: "",
		roleCategory: "",
		jobInterviewers: [],
		replacedEmployeeName: "",
		replacedEmployeeID: "",
		Remark: "",
	});
	const [functions, setFunctions] = useState([]);
	const [grades, setGrades] = useState([]);
	const [zones, setZones] = useState([]);
	const [jobLocations, setJobLocations] = useState([]);
	const [departments, setDepartments] = useState([]);
	const [roleType, setRoleType] = useState([]);
	const [jobCategory, setJobCategory] = useState([]);
	const [genderType, setGenderType] = useState([]);
	const [roleCategory, setRoleCategory] = useState([]);
	const [alert, setAlert] = useState(null);
	const [errors, setErrors] = useState({});
	const [error, setError] = useState("");
	const [isJobMenuOpen, setIsJobMenuOpen] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [newInterviewer, setNewInterviewer] = useState({
		interviewerCode: "",
		interviewerName: "",
		interviewSequence: 1,
	});
	const [modalInterviewers, setModalInterviewers] = useState([]);
	const [activeField, setActiveField] = useState("");
	const [suggestions, setSuggestions] = useState([]);
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [highlightedIndex, setHighlightedIndex] = useState(-1);
	const [profiles, setProfiles] = useState([]);

	const navigate = useNavigate();
	useEffect(() => {
		const fetchData = async () => {
			setFunctions(await getFunctions());
			setGrades(await getGrades());
			setZones(await getZones());
			setRoleType(await getRoleType());
			setJobCategory(await getJobCategory());
			setGenderType(await getGenderType());
			setRoleCategory(await getRoleCategory());
		};
		fetchData();
	}, []);

	useEffect(() => {
		if (formData.zone) {
			getJobLocations(formData.zone).then(setJobLocations);
		} else {
			setJobLocations([]);
		}
	}, [formData.zone]);

	// Open Modal
	const handleOpenModal = () => {
		setNewInterviewer({
			interviewerCode: "",
			interviewerName: "",
			interviewSequence: (formData.jobInterviewers.length || 0) + 1,
		});
		setShowModal(true);
	};

	// Save interviewers from modal → main form state
	const handleSaveInterviewers = () => {
		setFormData((prev) => ({
			...prev,
			jobInterviewers: [...prev.jobInterviewers, ...modalInterviewers],
		}));
		setShowModal(false);
	};

	//Department Dropdown
	useEffect(() => {
		const fetchDepartments = async () => {
			if (formData.function) {
				const result = await getDepartments(formData.function);
				setDepartments(result || []);
				setFormData((prev) => ({
					...prev,
					department: "", // reset when function changes
				}));
			}
		};
		fetchDepartments();
	}, [formData.function]);

	const handleChange = async (e, fieldName) => {
		if (typeof e === "string" && fieldName) {
			setFormData((prev) => ({
				...prev,
				[fieldName]: e,
			}));
		} else {
			const { name, value } = e.target;
			let newValue = value;

			if (
				[
					"numberOfOpenings",
					"minimumExperience",
					"offeredSalary",
					"maximumExperience",
				].includes(name) &&
				Number(value) < 0
			) {
				newValue = "1";
			}

			// If it's interviewerCode, update newInterviewer instead of formData
			if (name === "interviewerCode") {
				setNewInterviewer((prev) => ({
					...prev,
					interviewerCode: newValue,
				}));
			} else {
				setFormData((prev) => ({
					...prev,
					[name]: newValue,
				}));
			}

			if (name === "positionType") {
				if (value === "Replacement") {
					setFormData((prev) => ({
						...prev,
						positionType: value,
						numberOfOpenings: 1,
					}));
				} else {
					setFormData((prev) => ({
						...prev,
						positionType: value,
						replacedEmployeeName: "",
						replacedEmployeeID: "",
						numberOfOpenings: "",
					}));
				}
				return;
			}

			//  Handle autocomplete for interviewerCode as well
			if (
				(name === "reportingManager" ||
					name === "functionalManager" ||
					name === "replacedEmployeeName" ||
					name === "interviewerCode") &&
				value.trim().length >= 3
			) {
				try {
					const res = await getEmployeeSuggestions(value.trim());
					if (res && res.length > 0) {
						setActiveField(name);
						setSuggestions(res);
						setShowSuggestions(true);
						setHighlightedIndex(-1);
					} else {
						setSuggestions([]);
						setShowSuggestions(false);
					}
				} catch (err) {
					const safeMessage =
						err.response?.data?.message ||
						"Something went wrong. Please try again.";

					setAlert({
						type: "fail",
						message: safeMessage,
					});
					setSuggestions([]);
					setShowSuggestions(false);
				}
			} else {
				setSuggestions([]);
				setShowSuggestions(false);
			}
		}
	};

	const handleSelectSuggestion = (suggestion) => {
		if (activeField === "reportingManager") {
			setFormData((prev) => ({
				...prev,
				reportingManager: `${suggestion.employeeCode} - ${suggestion.employeeName}`,
				reportingManagerID: suggestion.employeeCode,
			}));
		} else if (activeField === "functionalManager") {
			setFormData((prev) => ({
				...prev,
				functionalManager: `${suggestion.employeeCode} - ${suggestion.employeeName}`,
				functionalManagerID: suggestion.employeeCode,
			}));
		} else if (activeField === "replacedEmployeeName") {
			setFormData((prev) => ({
				...prev,
				replacedEmployeeName: `${suggestion.employeeCode} - ${suggestion.employeeName}`,
				replacedEmployeeID: suggestion.employeeCode,
			}));
		} else if (activeField === "interviewerCode") {
			setNewInterviewer((prev) => ({
				...prev,
				interviewerCode: suggestion.employeeCode, // only code (for backend)
				interviewerName: suggestion.employeeName, // only name
				interviewerDisplay: `${suggestion.employeeCode} - ${suggestion.employeeName}`, // for UI
			}));
		}

		setShowSuggestions(false);
		setSuggestions([]);
		setHighlightedIndex(-1);
	};

	const handleKeyDown = (e) => {
		if (!showSuggestions || suggestions.length === 0) return;

		if (e.key === "ArrowDown") {
			e.preventDefault();
			setHighlightedIndex((prev) =>
				prev < suggestions.length - 1 ? prev + 1 : 0,
			);
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			setHighlightedIndex((prev) =>
				prev > 0 ? prev - 1 : suggestions.length - 1,
			);
		} else if (e.key === "Enter") {
			e.preventDefault();
			if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
				handleSelectSuggestion(suggestions[highlightedIndex]);
			}
		}
	};

	const validateForm = () => {
		const newErrors = {};

		const isEmpty = (value) =>
			value === undefined ||
			value === null ||
			(typeof value === "string" && value.trim() === "");

		if (isEmpty(formData.roleName))
			newErrors.roleName = "Job Title is required";
		if (isEmpty(formData.reportingManagerID))
			newErrors.reportingManagerID = "Reporting Manager ID is required";
		if (isEmpty(formData.functionalManagerID))
			newErrors.functionalManagerID = "Zonal Manager ID is required";
		if (isEmpty(formData.function)) newErrors.function = "Function is required";
		if (isEmpty(formData.grade)) newErrors.grade = "Grade is required";
		if (isEmpty(formData.zone)) newErrors.zone = "Zone is required";
		if (isEmpty(formData.jobLocation))
			newErrors.jobLocation = "Job Location is required";
		if (isEmpty(formData.offeredSalary))
			newErrors.offeredSalary = "Offered Salary is required";
		if (isEmpty(formData.minimumQualification))
			newErrors.minimumQualification = "Minimum Qualification is required";
		if (isEmpty(formData.minimumExperience))
			newErrors.minimumExperience = "Minimum Experience is required";
		if (isEmpty(formData.maximumExperience))
			newErrors.maximumExperience = "Maximum Experience is required";
		if (isEmpty(formData.genderPreference))
			newErrors.genderPreference = "Gender Preference is required";
		if (isEmpty(formData.roleCategory))
			newErrors.roleCategory = "Role Category is required";
		if (isEmpty(formData.positionType))
			newErrors.positionType = "Position Type is required";
		if (isEmpty(formData.requisitionDate))
			newErrors.requisitionDate = "Requisition Date is required";
		if (isEmpty(formData.jobDescription))
			newErrors.jobDescription = "Job Description is required";
		if (isEmpty(formData.department))
			newErrors.department = "Department is required";
		if (isEmpty(formData.roleType))
			newErrors.roleType = "Role Type is required";
		if (
			formData.numberOfOpenings === undefined ||
			formData.numberOfOpenings === null ||
			formData.numberOfOpenings === "" ||
			formData.numberOfOpenings === 0
		) {
			newErrors.numberOfOpenings = "Number of Openings is required";
		}
		if (isEmpty(formData.jobCategory))
			newErrors.jobCategory = "Job Category is required";
		if (
			formData.positionType === "Replacement" &&
			isEmpty(formData.replacedEmployeeID)
		) {
			newErrors.replacedEmployeeName = "Replaced Employee is required";
		}
		if (isEmpty(formData.Remark)) newErrors.Remark = "Remark is required";
		return newErrors;
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		const clientErrors = validateForm();
		if (Object.keys(clientErrors).length > 0) {
			setErrors(clientErrors);
			return;
		}

		try {
			const payload = {
				jobTitle: formData.roleName,
				reportingManagerID: formData.reportingManagerID,
				functionalManagerID: formData.functionalManagerID,
				function: formData.function,
				grade: formData.grade,
				zone: formData.zone,
				location: formData.jobLocation,
				offeredSalary: formData.offeredSalary,
				minimumQualification: formData.minimumQualification,
				minimumExperience: formData.minimumExperience,
				maximumExperience: formData.maximumExperience,
				genderPreference: formData.genderPreference,
				roleCategory: formData.roleCategory,
				positionType: formData.positionType,
				requisitionDate: formData.requisitionDate,
				jobDescription: formData.jobDescription,
				department: formData.department,
				numberOfOpenings: formData.numberOfOpenings,
				jobCategory: formData.jobCategory,
				roleType: formData.roleType,
				jobInterviewers: formData.jobInterviewers || [],
				replacedEmployee: formData.replacedEmployeeID,
				remark: formData.Remark,
			};
			const response = await createJobOpening(payload);

			if (response?.data?.status === "Success") {
				setAlert({
					type: "success",
					message: response.data.message || "Job opening created successfully!",
				});

				if (onSubmit) onSubmit(response.data || payload);

				setTimeout(() => {
					navigate("/Dashboard/Draft-Jobs");
				}, 1500);
			} else {
				setAlert({
					type: "fail",
					message: response?.data?.message || "Failed to create job opening.",
				});
			}
		} catch (error) {
			const errorMessage =
				error.response?.data?.message || "An unexpected error occurred.";
			setAlert({ type: "fail", message: errorMessage });
		}
	};

	const handleReset = () => {
		setFormData({
			jobTitle: "",
			reportingManagerID: "",
			reportingManager: "",
			functionalManagerID: "",
			functionalManager: "",
			function: "",
			functionID: 0,
			grade: "",
			roleName: "",
			jobCategory: "",
			zone: "",
			jobLocation: "",
			offeredSalary: "",
			minimumQualification: "",
			minimumExperience: "",
			maximumExperience: "",
			genderPreference: "",
			roleCategory: "",
			positionType: "",
			requisitionDate: "",
			jobDescription: "",
			department: "",
			numberOfOpenings: 0,
			roleType: "",
			jobInterviewers: [],
			replacedEmployeeName: "",
			replacedEmployeeID: "",
			Remark: "",
		});
		setErrors({});
		setError("");
	};

	const handleDragEnd = (result) => {
		if (!result.destination) return;

		const items = Array.from(formData.jobInterviewers);
		const [reorderedItem] = items.splice(result.source.index, 1);
		items.splice(result.destination.index, 0, reorderedItem);

		// Reassign interviewSequence
		const updatedItems = items.map((item, index) => ({
			...item,
			interviewSequence: index + 1, // Round numbers start from 1
		}));

		setFormData((prev) => ({
			...prev,
			jobInterviewers: updatedItems,
		}));
	};

	useEffect(() => {
		const fetchProfile = async () => {
			if (formData.grade && formData.department) {
				try {
					const res = await GetProfile(formData.grade, formData.department);

					if (res && res.length > 0) {
						setProfiles(res);
					} else {
						setProfiles([]);
					}
				} catch (error) {
					console.error("Error fetching profiles:", error);
					setProfiles([]);
				}
			} else {
				setProfiles([]);
			}
		};

		fetchProfile();
	}, [formData.grade, formData.department]);

	return (
		<>
			<div className="min-h-screen bg-[#f4f6f9]">
				{/* Top Banner */}
				<div
					className="w-full h-[200px] bg-cover bg-center"
					style={{ backgroundImage: `url(${bgImage})` }}
				/>

				{/* Form Wrapper */}
				<div className="flex justify-center -mt-20 pb-20 px-4">
					<div className="w-full max-w-5xl bg-white rounded-2xl shadow-xl p-8">
						<h3 className="text-center text-2xl font-semibold text-primary mb-8">
							CREATE NEW ROLE
						</h3>

						<form onSubmit={handleSubmit} className="space-y-8">
							{/*  JOB DETAILS  */}
							<div className="border rounded-lg">
								<div className="bg-master-header text-white px-4 py-3 font-medium">
									Role Information
								</div>

								<div className="p-6 space-y-6">
									<div>
										<label className="block text-sm font-medium mb-2">
											Role Category <span className="text-red-500">*</span>
										</label>

										<div className="flex gap-6">
											{roleCategory.map((rc) => (
												<label
													key={rc.id}
													className="flex items-center gap-2 cursor-pointer"
												>
													<input
														type="radio"
														name="roleCategory"
														value={rc.roleCategory}
														checked={formData.roleCategory === rc.roleCategory}
														onChange={handleChange}
														className="accent-blue-600"
													/>
													{rc.roleCategory}
												</label>
											))}
										</div>

										{errors.roleCategory && (
											<p className="text-red-500 text-sm mt-1">
												{errors.roleCategory}
											</p>
										)}
									</div>

									<div className="grid md:grid-cols-2 gap-6">
										<div>
											<label className="block text-sm font-medium mb-1">
												Position Category{" "}
												<span className="text-red-500">*</span>
											</label>
											<select
												name="jobCategory"
												value={formData.jobCategory || ""}
												onChange={handleChange}
												className={`w-full border ${
													errors.jobCategory
														? "border-red-500"
														: "border-gray-300"
												} rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
											>
												<option value="">Select</option>
												{jobCategory.map((j, index) => (
													<option key={index} value={j.jobCategoryID}>
														{j.jobCategory}
													</option>
												))}
											</select>
											{errors.jobCategory && (
												<p className="text-red-500 text-sm mt-1">
													{errors.jobCategory}
												</p>
											)}
										</div>

										<div>
											<label className="block text-sm font-medium mb-1">
												Position Type <span className="text-red-500">*</span>
											</label>
											<select
												name="positionType"
												value={formData.positionType || ""}
												onChange={handleChange}
												className={`w-full border ${
													errors.positionType
														? "border-red-500"
														: "border-gray-300"
												} rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
											>
												<option value="">Select</option>
												<option value="Replacement">Replacement</option>
												<option value="New_Opening">New Opening</option>
											</select>
											{errors.positionType && (
												<p className="text-red-500 text-sm mt-1">
													{errors.positionType}
												</p>
											)}
										</div>

										{formData.positionType === "Replacement" && (
											<div className="relative">
												<label className="block text-sm font-medium mb-1">
													Replaced Employee{" "}
													<span className="text-red-500">*</span>
												</label>

												<input
													name="replacedEmployeeName"
													value={formData.replacedEmployeeName || ""}
													onChange={handleChange}
													onFocus={() => {
														setActiveField("replacedEmployeeName");
														setShowSuggestions(true);
													}}
													onBlur={() =>
														setTimeout(() => setShowSuggestions(false), 200)
													}
													onKeyDown={(e) => handleKeyDown(e)}
													autoComplete="off"
													className={`w-full border ${
														errors.replacedEmployeeName
															? "border-red-500"
															: "border-gray-300"
													} rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
												/>

												{showSuggestions &&
													activeField === "replacedEmployeeName" &&
													suggestions.length > 0 && (
														<ul className="absolute top-full left-0 w-full mt-1 bg-white border rounded-md shadow-xl max-h-48 overflow-y-auto z-[9999]">
															{suggestions.map((s, idx) => (
																<li
																	key={idx}
																	onClick={() => handleSelectSuggestion(s)}
																	className={`px-3 py-2 cursor-pointer ${
																		highlightedIndex === idx
																			? "bg-blue-500 text-white"
																			: "hover:bg-gray-100"
																	}`}
																>
																	{s.employeeCode} - {s.employeeName}
																</li>
															))}
														</ul>
													)}
											</div>
										)}

										{/* Requisition Date */}
										<div className="relative">
											<label className="block text-sm font-medium mb-1">
												Requisition Date <span className="text-red-500">*</span>
											</label>
											<input
												type="date"
												name="requisitionDate"
												value={formData.requisitionDate || ""}
												onChange={handleChange}
												max={new Date().toISOString().split("T")[0]}
												className={`w-full border ${
													errors.requisitionDate
														? "border-red-500"
														: "border-gray-300"
												} rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
											/>
											{errors.requisitionDate && (
												<p className="text-red-500 text-sm mt-1">
													{errors.requisitionDate}
												</p>
											)}
										</div>
									</div>

									<hr className="my-6 border-gray-300" />

									<div className="grid md:grid-cols-2 gap-6">
										<div>
											<label className="block text-sm font-medium mb-1">
												Function <span className="text-red-500">*</span>
											</label>
											<select
												name="function"
												value={formData.function || ""}
												onChange={handleChange}
												className={`w-full border ${
													errors.function ? "border-red-500" : "border-gray-300"
												} rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
											>
												<option value="">Select</option>
												{functions.map((f, index) => (
													<option key={index} value={f.functionID}>
														{f.functionName}
													</option>
												))}
											</select>
											{errors.function && (
												<p className="text-red-500 text-sm mt-1">
													{errors.function}
												</p>
											)}
										</div>

										<div>
											<label className="block text-sm font-medium mb-1">
												Department <span className="text-red-500">*</span>
											</label>
											<select
												name="department"
												value={formData.department || ""}
												onChange={handleChange}
												className={`w-full border ${
													errors.department
														? "border-red-500"
														: "border-gray-300"
												} rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
											>
												<option value="">Select</option>
												{departments.map((dept, index) => (
													<option key={index} value={dept.departmentName}>
														{dept.departmentName}
													</option>
												))}
											</select>
											{errors.department && (
												<p className="text-red-500 text-sm mt-1">
													{errors.department}
												</p>
											)}
										</div>
										<div>
											<label className="block text-sm font-medium mb-1">
												Grade <span className="text-red-500">*</span>
											</label>
											<select
												name="grade"
												value={formData.grade || ""}
												onChange={handleChange}
												className={`w-full border ${
													errors.grade ? "border-red-500" : "border-gray-300"
												} rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
											>
												<option value="">Select</option>
												{grades.map((g, index) => (
													<option key={index} value={g.grade}>
														{g.gradeName}
													</option>
												))}
											</select>
											{errors.grade && (
												<p className="text-red-500 text-sm mt-1">
													{errors.grade}
												</p>
											)}
										</div>
										<div>
											<label className="block text-sm font-medium mb-1">
												Profile <span className="text-red-500">*</span>
											</label>

											<select
												name="roleName"
												value={formData.roleName || ""}
												onChange={handleChange}
												className={`w-full border ${
													errors.roleName ? "border-red-500" : "border-gray-300"
												} rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
											>
												<option value="">Select Profile</option>

												{profiles.map((item, index) => (
													<option key={index} value={item.profileName}>
														{item.profileName}
													</option>
												))}
											</select>

											{errors.roleName && (
												<p className="text-red-500 text-sm mt-1">
													{errors.roleName}
												</p>
											)}
										</div>
									</div>

									{/* Job Description */}
									<div>
										<label className="block text-sm font-medium mb-1">
											Job Description (PDF Only){" "}
											<span className="text-red-500">*</span>
										</label>

										<input
											type="file"
											accept="application/pdf"
											className={`w-full border ${
												errors.jobDescription
													? "border-red-500"
													: "border-gray-300"
											} rounded-md px-3 py-2`}
											onChange={(e) => {
												const file = e.target.files[0];
												const MAX_FILE_SIZE = 5 * 1024 * 1024;

												if (file) {
													if (file.type !== "application/pdf") {
														setErrors((prev) => ({
															...prev,
															jobDescription: "Only PDF files are allowed",
														}));
														return;
													}

													if (file.size > MAX_FILE_SIZE) {
														setAlert({
															type: "fail",
															message: "File size must be less than 5MB",
														});
														return;
													}

													setFormData((prev) => ({
														...prev,
														jobDescription: file,
													}));
												}
											}}
										/>

										{errors.jobDescription && (
											<p className="text-red-500 text-sm mt-1">
												{errors.jobDescription}
											</p>
										)}
									</div>
								</div>
							</div>

							{/*  ORGANIZATIONAL INFORMATION  */}
							<div className="border rounded-lg overflow-hidden">
								<div className="bg-master-header text-white px-4 py-3 font-medium">
									Manager Information
								</div>

								<div className="p-6 grid md:grid-cols-2 gap-6">
									<div className="relative">
										<label className="block text-sm font-medium mb-1">
											Functional Manager <span className="text-red-500">*</span>
										</label>

										<input
											name="functionalManager"
											value={formData.functionalManager || ""}
											onChange={handleChange}
											onBlur={() =>
												setTimeout(() => setShowSuggestions(false), 200)
											}
											onKeyDown={(e) => handleKeyDown(e)}
											autoComplete="off"
											className={`w-full border ${
												errors.functionalManagerID
													? "border-red-500"
													: "border-gray-300"
											} rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
										/>

										{errors.functionalManagerID && (
											<p className="text-red-500 text-sm mt-1">
												{errors.functionalManagerID}
											</p>
										)}

										{/*  Suggestions Dropdown */}
										{showSuggestions &&
											activeField === "functionalManager" &&
											suggestions.length > 0 && (
												<ul className="absolute left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto z-50">
													{suggestions.map((s, idx) => (
														<li
															key={idx}
															onClick={() => handleSelectSuggestion(s)}
															className={`px-3 py-2 cursor-pointer ${
																highlightedIndex === idx
																	? "bg-blue-500 text-white"
																	: "hover:bg-gray-100"
															}`}
														>
															{s.employeeCode} - {s.employeeName}
														</li>
													))}
												</ul>
											)}
									</div>

									{/* Reporting Manager */}
									<div className="relative">
										<label className="block text-sm font-medium mb-1">
											Reporting Manager <span className="text-red-500">*</span>
										</label>

										<input
											name="reportingManager"
											value={formData.reportingManager || ""}
											onChange={handleChange}
											onBlur={() =>
												setTimeout(() => setShowSuggestions(false), 200)
											}
											onKeyDown={(e) => handleKeyDown(e)}
											autoComplete="off"
											className={`w-full border ${
												errors.reportingManagerID
													? "border-red-500"
													: "border-gray-300"
											} rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
										/>

										{errors.reportingManagerID && (
											<p className="text-red-500 text-sm mt-1">
												{errors.reportingManagerID}
											</p>
										)}

										{/*  Suggestions Dropdown */}
										{showSuggestions &&
											activeField === "reportingManager" &&
											suggestions.length > 0 && (
												<ul className="absolute left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto z-50">
													{suggestions.map((s, idx) => (
														<li
															key={idx}
															onClick={() => handleSelectSuggestion(s)}
															className={`px-3 py-2 cursor-pointer ${
																highlightedIndex === idx
																	? "bg-blue-500 text-white"
																	: "hover:bg-gray-100"
															}`}
														>
															{s.employeeCode} - {s.employeeName}
														</li>
													))}
												</ul>
											)}
									</div>
								</div>
							</div>

							{/*  LOCATION DETAILS  */}
							<div className="border rounded-lg overflow-hidden">
								<div className="bg-master-header text-white px-4 py-3 font-medium">
									Location Information
								</div>

								<div className="p-6 grid md:grid-cols-2 gap-6">
									{/* Zone */}
									<div>
										<label className="block text-sm font-medium mb-1">
											Zone <span className="text-red-500">*</span>
										</label>
										<select
											name="zone"
											value={formData.zone || ""}
											onChange={handleChange}
											className={`w-full border ${
												errors.zone ? "border-red-500" : "border-gray-300"
											} rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
										>
											<option value="">Select</option>
											{zones.map((z, index) => (
												<option key={index} value={z.zoneID}>
													{z.zoneName}
												</option>
											))}
										</select>
										{errors.zone && (
											<p className="text-red-500 text-sm mt-1">{errors.zone}</p>
										)}
									</div>

									{/* Job Location */}
									<div>
										<label className="block text-sm font-medium mb-1">
											Job Location <span className="text-red-500">*</span>
										</label>
										<select
											name="jobLocation"
											value={formData.jobLocation || ""}
											onChange={handleChange}
											className={`w-full border ${
												errors.jobLocation
													? "border-red-500"
													: "border-gray-300"
											} rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
										>
											<option value="">Select</option>
											{jobLocations.map((j, index) => (
												<option key={index} value={j.locationID}>
													{j.jobLocation}
												</option>
											))}
										</select>
										{errors.jobLocation && (
											<p className="text-red-500 text-sm mt-1">
												{errors.jobLocation}
											</p>
										)}
									</div>
								</div>
							</div>

							{/*  CANDIDATE REQUIREMENTS  */}
							<div className="border rounded-lg overflow-hidden">
								<div className="bg-master-header text-white px-4 py-3 font-medium">
									Candidate Requirements
								</div>

								<div className="p-6 grid md:grid-cols-2 gap-6">
									{/* Minimum Qualification */}
									<div>
										<label className="block text-sm font-medium mb-1">
											Minimum Qualification{" "}
											<span className="text-red-500">*</span>
										</label>
										<input
											name="minimumQualification"
											value={formData.minimumQualification || ""}
											onChange={handleChange}
											className={`w-full border ${
												errors.minimumQualification
													? "border-red-500"
													: "border-gray-300"
											} rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
										/>
										{errors.minimumQualification && (
											<p className="text-red-500 text-sm mt-1">
												{errors.minimumQualification}
											</p>
										)}
									</div>

									{/* Minimum Experience */}
									<div>
										<label className="block text-sm font-medium mb-1">
											Minimum Experience (Years){" "}
											<span className="text-red-500">*</span>
										</label>
										<input
											type="number"
											name="minimumExperience"
											value={formData.minimumExperience || ""}
											onChange={handleChange}
											className={`w-full border ${
												errors.minimumExperience
													? "border-red-500"
													: "border-gray-300"
											} rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
										/>
										{errors.minimumExperience && (
											<p className="text-red-500 text-sm mt-1">
												{errors.minimumExperience}
											</p>
										)}
									</div>

									<div>
										<label className="block text-sm font-medium mb-1">
											Maximum Experience (Years){" "}
											<span className="text-red-500">*</span>
										</label>

										<input
											type="number"
											name="maximumExperience"
											value={formData.maximumExperience || ""}
											onChange={handleChange}
											className={`w-full border ${
												errors.maximumExperience
													? "border-red-500"
													: "border-gray-300"
											} rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
										/>

										{errors.maximumExperience && (
											<p className="text-red-500 text-sm mt-1">
												{errors.maximumExperience}
											</p>
										)}
									</div>

									{/* Gender */}
									<div>
										<label className="block text-sm font-medium mb-1">
											Gender Preference <span className="text-red-500">*</span>
										</label>
										<select
											name="genderPreference"
											value={formData.genderPreference || ""}
											onChange={handleChange}
											className={`w-full border ${
												errors.genderPreference
													? "border-red-500"
													: "border-gray-300"
											} rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
										>
											<option value="">Select</option>
											{genderType.map((g, index) => (
												<option key={index} value={g.genderType}>
													{g.genderType}
												</option>
											))}
										</select>
										{errors.genderPreference && (
											<p className="text-red-500 text-sm mt-1">
												{errors.genderPreference}
											</p>
										)}
									</div>

									{/* Role Type */}
									<div>
										<label className="block text-sm font-medium mb-1">
											Role Type <span className="text-red-500">*</span>
										</label>
										<select
											name="roleType"
											value={formData.roleType || ""}
											onChange={handleChange}
											className={`w-full border ${
												errors.roleType ? "border-red-500" : "border-gray-300"
											} rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
										>
											<option value="">Select</option>
											{roleType.map((r, index) => (
												<option key={index} value={r.roleTypeID}>
													{r.roleType}
												</option>
											))}
										</select>
										{errors.roleType && (
											<p className="text-red-500 text-sm mt-1">
												{errors.roleType}
											</p>
										)}
									</div>

									<div className="md:col-span-2">
										<label className="block text-sm font-medium mb-1">
											Remarks / Specifications{" "}
											<span className="text-red-500">*</span>
										</label>

										<ReactQuill
											theme="snow"
											value={formData.Remark || ""}
											onChange={(content) => handleChange(content, "Remark")}
											className="bg-white rounded-md mb-12"
											style={{ height: "160px" }}
										/>

										{errors.Remark && (
											<p className="text-red-500 text-sm mt-1">
												{errors.Remark}
											</p>
										)}
									</div>
								</div>
							</div>

							{/*  COMPENSATION & OPENINGS  */}
							<div className="border rounded-lg overflow-hidden">
								<div className="bg-master-header text-white px-4 py-3 font-medium">
									Compensation & Openings
								</div>

								<div className="p-6 grid md:grid-cols-2 gap-6">
									{/* Number of Openings */}
									<div>
										<label className="block text-sm font-medium mb-1">
											Number of Openings <span className="text-red-500">*</span>
										</label>
										<input
											type="number"
											name="numberOfOpenings"
											value={formData.numberOfOpenings || ""}
											onChange={handleChange}
											disabled={formData.positionType === "Replacement"}
											className={`w-full border ${
												errors.numberOfOpenings
													? "border-red-500"
													: "border-gray-300"
											} rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
												formData.positionType === "Replacement"
													? "bg-gray-100 cursor-not-allowed"
													: ""
											}`}
										/>
										{errors.numberOfOpenings && (
											<p className="text-red-500 text-sm mt-1">
												{errors.numberOfOpenings}
											</p>
										)}
									</div>

									{/* Maximum Budget */}
									<div>
										<label className="block text-sm font-medium mb-1">
											Maximum Budget (in CTC)
											<span className="text-red-500">*</span>
										</label>
										<input
											type="number"
											name="offeredSalary"
											value={formData.offeredSalary || ""}
											onChange={handleChange}
											className={`w-full border ${
												errors.offeredSalary
													? "border-red-500"
													: "border-gray-300"
											} rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500`}
										/>
										{errors.offeredSalary && (
											<p className="text-red-500 text-sm mt-1">
												{errors.offeredSalary}
											</p>
										)}
									</div>
								</div>
							</div>

							{/*  INTERVIEW PANEL  */}
							<div className="border rounded-lg overflow-hidden">
								<div className="bg-master-header text-white px-4 py-3 font-medium">
									Interview Panel
								</div>

								<div className="p-6 space-y-4">
									<button
										type="button"
										onClick={handleOpenModal}
										className="bg-primary text-white px-4 py-2 rounded-md transition"
									>
										➕ Add Interviewer
									</button>

									<DragDropContext onDragEnd={handleDragEnd}>
										<Droppable droppableId="interviewers-list">
											{(provided) => (
												<ul
													{...provided.droppableProps}
													ref={provided.innerRef}
													className="space-y-2"
												>
													{formData.jobInterviewers.map((intv, index) => (
														<Draggable
															key={intv.interviewerCode.toString()}
															draggableId={intv.interviewerCode.toString()}
															index={index}
														>
															{(provided) => (
																<li
																	ref={provided.innerRef}
																	{...provided.draggableProps}
																	{...provided.dragHandleProps}
																	className="flex justify-between items-center border rounded-md px-4 py-2 bg-gray-50"
																>
																	<span>
																		{intv.interviewerDisplay ||
																			`${intv.interviewerCode} - ${intv.interviewerName}`}{" "}
																		(Round: {intv.interviewSequence})
																	</span>

																	<button
																		type="button"
																		onClick={() =>
																			setFormData((prev) => ({
																				...prev,
																				jobInterviewers:
																					prev.jobInterviewers.filter(
																						(_, idx) => idx !== index,
																					),
																			}))
																		}
																		className="text-red-500 hover:text-red-700"
																	>
																		<Trash2 size={18} />
																	</button>
																</li>
															)}
														</Draggable>
													))}
													{provided.placeholder}
												</ul>
											)}
										</Droppable>
									</DragDropContext>

									{formData.jobInterviewers.length === 0 && (
										<p className="text-center text-gray-500">
											No interviewers added yet.
										</p>
									)}
								</div>
							</div>

							{/*  BUTTONS  */}
							<div className="flex justify-center gap-6 pt-6">
								<button
									type="submit"
									className="bg-primary text-white px-8 py-2 rounded-md hover:bg-[#174d91] transition"
								>
									GENERATE ROLE
								</button>

								<button
									type="button"
									onClick={handleReset}
									className="border border-gray-400 text-gray-700 px-8 py-2 rounded-md hover:bg-gray-100 transition"
								>
									Reset
								</button>
							</div>
						</form>
					</div>
				</div>
			</div>

			<AddInterviewerModal
				show={showModal}
				onClose={() => setShowModal(false)}
				newInterviewer={newInterviewer}
				setNewInterviewer={setNewInterviewer}
				handleChange={handleChange}
				handleKeyDown={handleKeyDown}
				handleSelectSuggestion={handleSelectSuggestion}
				showSuggestions={showSuggestions}
				activeField={activeField}
				suggestions={suggestions}
				highlightedIndex={highlightedIndex}
				setFormData={setFormData}
				formData={formData}
				setShowSuggestions={setShowSuggestions}
			/>

			{alert && (
				<NormalAlert
					type={alert.type}
					message={alert.message}
					onClose={() => setAlert(null)}
				/>
			)}
		</>
	);
};

export default PostJobForm;
